// Iter 9.50+ R19 - Inspo v2 (renamed folders) + Confirmation v2 (add-ons,
// pricing, tips, indicators) + Preference label reference + Host Insights
// (Spotify Wrapped style). All static design previews.
import { useSearchParams, Link } from "react-router-dom";
import {
  ArrowLeft, ArrowUp, ArrowUpRight, Award, Bell, Calendar as CalendarIcon, Camera,
  Check, ChevronDown, ChevronRight, Clock, Copy, CreditCard, DollarSign, Edit,
  ExternalLink, Eye, EyeOff, Folder, Globe, Heart, Home, Image as ImageIcon, Info,
  Link2, Lock, Mail, MapPin, MoreVertical, Package, Plus, RefreshCw, Search,
  Settings, Share2, Smartphone, Sparkles, Star, Tag as TagIcon, ToggleLeft,
  TrendingUp, Trash2, Upload, User, Users, Wallet, Zap, BarChart3, Hash,
} from "lucide-react";

const FRAME = "max-w-[420px] mx-auto bg-white min-h-screen shadow-xl";
const Header = ({ title, back, right }) => (
  <header className="sticky top-0 z-20 bg-white border-b border-stone-200 px-4 h-14 flex items-center justify-between">
    {back ? <Link to={back} className="w-8 h-8 -ml-1 flex items-center justify-center text-stone-700 hover:bg-stone-100 rounded-full"><ArrowLeft className="w-4 h-4" /></Link> : <div className="w-8 h-8" />}
    <h1 className="text-sm font-bold text-stone-900 uppercase tracking-wider">{title}</h1>
    <div className="w-8 h-8 flex items-center justify-center">{right || null}</div>
  </header>
);
const StateSwitcher = ({ flow, states, current }) => (
  <div className="bg-stone-100 border-t border-stone-200 px-3 py-2 flex flex-wrap gap-1 sticky bottom-0">
    <span className="text-[9px] uppercase tracking-wider text-stone-500 font-bold mr-2 self-center">States</span>
    {states.map((s) => (
      <Link key={s.id} to={`/design/${flow}?s=${s.id}`}
        className={`text-[10px] px-2 py-1 rounded-full ${current === s.id ? "bg-stone-900 text-white" : "bg-white border border-stone-200 text-stone-600"}`}>
        {s.label}
      </Link>
    ))}
  </div>
);

// ============================================================================
// 1) INSPO v2 - renamed folders (face/hair/nails/lashes/style) with nested
//    experience subfolders + Before/Final inside each + eye icons.
// ============================================================================
const INSPO2_STATES = [
  { id: "root", label: "Root (5 cats)" },
  { id: "face", label: "Inside Face" },
  { id: "face-makeup-exp", label: "Face -> Makeup experiences" },
  { id: "exp-detail", label: "Experience folder" },
];

function InspoV2Design() {
  const [params] = useSearchParams();
  const s = params.get("s") || "root";
  return (
    <div className={FRAME}>
      <Header title="Inspo" back="/design" right={<button className="text-stone-700"><Search className="w-4 h-4" /></button>} />
      <div className="px-4 pt-3 pb-32 space-y-4 bg-stone-50">
        {s === "root" && <Inspo2Root />}
        {s === "face" && <Inspo2Face />}
        {s === "face-makeup-exp" && <Inspo2MakeupExperiences />}
        {s === "exp-detail" && <Inspo2ExperienceDetail />}
      </div>
      <StateSwitcher flow="inspo-v2" states={INSPO2_STATES} current={s} />
    </div>
  );
}

const ROOT_FOLDERS = [
  { name: "Face", count: 64, slug: "face", img: "https://images.unsplash.com/photo-1599733589046-77fde6f1f5b6?w=300", sub: 4 },
  { name: "Hair", count: 38, slug: "root", img: "https://images.unsplash.com/photo-1605497788044-5a32c7078486?w=300", sub: 3 },
  { name: "Nails", count: 22, slug: "root", img: "https://images.unsplash.com/photo-1604654894611-6973b376cbde?w=300", sub: 2 },
  { name: "Lashes", count: 15, slug: "root", img: "https://images.unsplash.com/photo-1583241800698-9c2e89f1d2f4?w=300", sub: 2 },
  { name: "Style", count: 41, slug: "root", img: "https://images.unsplash.com/photo-1483985988355-763728e1935b?w=300", sub: 3 },
];
const Inspo2Root = () => (
  <div className="space-y-3" data-testid="state-inspo2-root">
    <div className="flex items-center justify-between">
      <p className="text-xs text-stone-500">5 categories - 180 pins total</p>
      <button className="text-[11px] text-purple-700 font-semibold flex items-center gap-1"><Plus className="w-3 h-3" /> Folder</button>
    </div>
    <div className="grid grid-cols-2 gap-3">
      {ROOT_FOLDERS.map((c) => (
        <Link key={c.name} to={`/design/inspo-v2?s=${c.slug}`} className="rounded-2xl overflow-hidden border border-stone-200 bg-white" data-testid={`inspo2-folder-${c.name.toLowerCase()}`}>
          <div className="aspect-square bg-cover bg-center" style={{ backgroundImage: `url(${c.img})` }} />
          <div className="p-2.5">
            <p className="text-xs font-bold text-stone-900">{c.name}</p>
            <p className="text-[10px] text-stone-500">{c.count} pins - {c.sub} folders</p>
          </div>
        </Link>
      ))}
    </div>
  </div>
);

const Inspo2Face = () => (
  <div className="space-y-3" data-testid="state-inspo2-face">
    <Link to="/design/inspo-v2?s=root" className="text-[11px] text-stone-500 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> All categories</Link>
    <div className="flex items-center justify-between">
      <div>
        <h3 className="text-lg font-bold text-stone-900">Face</h3>
        <p className="text-[11px] text-stone-500">64 pins - 4 folders</p>
      </div>
      <button className="text-[11px] text-purple-700 font-semibold flex items-center gap-1"><Plus className="w-3 h-3" /> New folder</button>
    </div>
    {/* User folders */}
    <p className="text-[9px] uppercase tracking-wider text-stone-500 font-bold pt-2">Your folders</p>
    <div className="grid grid-cols-2 gap-3">
      {[
        { name: "Soft glam", count: 12 },
        { name: "Editorial", count: 8 },
      ].map((f) => (
        <div key={f.name} className="rounded-2xl border border-stone-200 bg-white p-3">
          <div className="grid grid-cols-3 gap-px aspect-[3/2] mb-2 rounded-md overflow-hidden">
            {[0,1,2].map(i => <div key={i} className="bg-rose-100" />)}
          </div>
          <p className="text-xs font-bold text-stone-900">{f.name}</p>
          <p className="text-[10px] text-stone-500">{f.count} pins</p>
        </div>
      ))}
    </div>
    {/* Phygital shared sub-folders - Makeup + Skin routine experiences */}
    <p className="text-[9px] uppercase tracking-wider text-purple-700 font-bold pt-3">Phygital experiences in Face</p>
    {[
      { name: "Makeup experiences", count: 18, sub: 6, slug: "face-makeup-exp", icon: Sparkles },
      { name: "Skin routine experiences", count: 12, sub: 4, slug: "face-makeup-exp", icon: RefreshCw },
    ].map((f) => (
      <Link key={f.name} to={`/design/inspo-v2?s=${f.slug}`} className="block rounded-2xl border border-purple-200 bg-purple-50 p-3" data-testid={`inspo2-face-${f.name.toLowerCase().replace(/ /g,'-')}`}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-purple-200 flex items-center justify-center"><f.icon className="w-5 h-5 text-purple-700" /></div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-stone-900">{f.name}</p>
            <p className="text-[10px] text-purple-700">{f.sub} experiences - {f.count} photos</p>
          </div>
          <ChevronRight className="w-4 h-4 text-purple-500" />
        </div>
      </Link>
    ))}
  </div>
);

const Inspo2MakeupExperiences = () => (
  <div className="space-y-3" data-testid="state-inspo2-makeup-experiences">
    <Link to="/design/inspo-v2?s=face" className="text-[11px] text-stone-500 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Face</Link>
    <div>
      <h3 className="text-lg font-bold text-stone-900">Makeup experiences</h3>
      <p className="text-[11px] text-stone-500">6 experiences - 18 photos</p>
    </div>
    {[
      { name: "Sister's wedding glam", date: "Jun 15", count: 4, before: 1, final: 3, status: "completed", img: "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=300" },
      { name: "Birthday party", date: "May 22", count: 5, before: 1, final: 4, status: "completed", img: "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=300" },
      { name: "Engagement shoot", date: "May 04", count: 3, before: 1, final: 2, status: "completed", img: "https://images.unsplash.com/photo-1492106087820-71f1a00d2b11?w=300" },
      { name: "Editorial test", date: "Apr 18", count: 2, before: 0, final: 2, status: "completed", img: "https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=300" },
      { name: "Soft glam refresh", date: "Apr 03", count: 0, before: 0, final: 0, status: "planning", img: null },
    ].map((e, i) => (
      <Link key={i} to="/design/inspo-v2?s=exp-detail" className="block rounded-2xl border border-stone-200 bg-white p-3" data-testid={`inspo2-exp-${i}`}>
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-xl bg-stone-200 overflow-hidden flex-shrink-0 flex items-center justify-center">
            {e.img ? <img src={e.img} alt="" className="w-full h-full object-cover" /> : <Sparkles className="w-5 h-5 text-stone-400" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="text-xs font-bold text-stone-900 truncate">{e.name}</p>
              {e.status === "planning" && <span className="text-[7px] uppercase tracking-wider bg-amber-100 text-amber-700 px-1 py-0.5 rounded font-bold">Planning</span>}
            </div>
            <p className="text-[10px] text-stone-500">{e.date} - {e.before} before - {e.final} final</p>
          </div>
          {/* EYE icon - links to that experience's preference card */}
          <button className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200" data-testid={`exp-eye-${i}`}>
            <Eye className="w-3.5 h-3.5 text-stone-600" />
          </button>
        </div>
      </Link>
    ))}
    <p className="text-[10px] text-stone-500 italic px-1">Tip: tap the eye on any experience to open its preference card. Visibility is controlled per card.</p>
  </div>
);

const Inspo2ExperienceDetail = () => (
  <div className="space-y-3" data-testid="state-inspo2-exp-detail">
    <Link to="/design/inspo-v2?s=face-makeup-exp" className="text-[11px] text-stone-500 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Makeup experiences</Link>
    <div className="flex items-start justify-between">
      <div>
        <h3 className="text-lg font-bold text-stone-900">Sister&apos;s wedding glam</h3>
        <p className="text-[11px] text-stone-500">Jun 15 - 1 before - 3 final - by Shelsea P.</p>
      </div>
      <Link to="/design/preference-card?s=after-multiple" className="w-9 h-9 rounded-full bg-stone-900 flex items-center justify-center" data-testid="exp-detail-eye">
        <Eye className="w-4 h-4 text-white" />
      </Link>
    </div>
    <p className="text-[9px] uppercase tracking-wider text-stone-500 font-bold pt-2">Before (1)</p>
    <div className="grid grid-cols-3 gap-1.5">
      <div className="relative aspect-square rounded-md bg-cover" style={{backgroundImage:"url(https://images.unsplash.com/photo-1614283233556-f35b0c801ef1?w=300)"}}>
        <span className="absolute top-1 left-1 text-[8px] uppercase tracking-wider bg-amber-500 text-white px-1 py-0.5 rounded font-bold">Before</span>
      </div>
    </div>
    <p className="text-[9px] uppercase tracking-wider text-stone-500 font-bold pt-2">Final looks (3 of 5)</p>
    <div className="grid grid-cols-3 gap-1.5">
      {[
        "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=300",
        "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=300",
        "https://images.unsplash.com/photo-1492106087820-71f1a00d2b11?w=300",
      ].map((u, i) => (
        <div key={i} className="relative aspect-square rounded-md bg-cover" style={{backgroundImage:`url(${u})`}}>
          <span className="absolute top-1 left-1 text-[8px] uppercase tracking-wider bg-emerald-600 text-white px-1 py-0.5 rounded font-bold">Final</span>
          <span className="absolute bottom-1 left-1 text-[7px] bg-white/95 text-stone-700 px-1 rounded">{i < 2 ? "By Shelsea" : "By Aria"}</span>
        </div>
      ))}
    </div>
    <button className="w-full py-2.5 rounded-full border border-dashed border-stone-300 text-stone-600 text-xs font-semibold flex items-center justify-center gap-1.5">
      <Upload className="w-3 h-3" /> Add to this experience
    </button>
  </div>
);

// ============================================================================
// 2) CONFIRMATION v2 - add-ons, pricing breakdown, tips, products, indicators
// ============================================================================
const CONF2_STATES = [
  { id: "guest", label: "Guest view" },
  { id: "host", label: "Host control view" },
  { id: "explainer", label: "Section explainer popup" },
];
function ConfirmationV2Design() {
  const [params] = useSearchParams();
  const s = params.get("s") || "guest";
  return (
    <div className={FRAME}>
      <Header title="Confirmation" back="/design" right={<MoreVertical className="w-4 h-4 text-stone-500" />} />
      <div className="px-4 pt-3 pb-32 space-y-3 bg-stone-50 relative">
        {s === "guest" && <Conf2Guest />}
        {s === "host" && <Conf2Host />}
        {s === "explainer" && <Conf2Explainer />}
      </div>
      <StateSwitcher flow="confirmation-v2" states={CONF2_STATES} current={s} />
    </div>
  );
}

const SectionWithIndicator = ({ title, indicator, children, headerRight }) => (
  <div className="rounded-2xl bg-white border border-stone-200 p-4 space-y-2.5">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1.5">
        <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">{title}</p>
        {indicator && (
          <button className="w-4 h-4 rounded-full bg-stone-100 flex items-center justify-center" title={indicator} data-testid={`indicator-${title.toLowerCase().replace(/ /g,'-')}`}>
            <Info className="w-2.5 h-2.5 text-stone-500" />
          </button>
        )}
      </div>
      {headerRight}
    </div>
    {children}
  </div>
);

const Conf2Guest = () => (
  <div className="space-y-3" data-testid="state-conf2-guest">
    {/* Event pass strip */}
    <div className="rounded-2xl bg-stone-900 text-white p-4">
      <p className="text-[9px] uppercase tracking-wider opacity-70 font-bold">Event pass - Confirmed</p>
      <p className="text-lg font-bold mt-1">Sister&apos;s wedding glam</p>
      <p className="text-[11px] opacity-80">Privé Beauté - Jun 15 at 10:30 AM</p>
    </div>

    {/* Add-ons available pre-service */}
    <SectionWithIndicator title="Add-ons available" indicator="Things you can add to your booking. Host curates these.">
      {[
        { name: "Strip lashes", price: 25, time: "+10 min", preview: "https://images.unsplash.com/photo-1583241800698-9c2e89f1d2f4?w=200", on: true },
        { name: "Glow Powder Touch-up Kit", price: 18, time: "Take home", preview: "https://images.unsplash.com/photo-1631214500099-1b0d8db6e0bd?w=200", on: false },
        { name: "Brow shape + tint", price: 35, time: "+15 min", preview: "https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=200", on: true },
      ].map((a, i) => (
        <div key={i} className={`rounded-xl border p-2.5 flex items-center gap-3 ${a.on ? "border-emerald-300 bg-emerald-50" : "border-stone-200 bg-white"}`} data-testid={`addon-${i}`}>
          <div className="w-12 h-12 rounded-lg bg-cover" style={{backgroundImage:`url(${a.preview})`}} />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-stone-900">{a.name}</p>
            <p className="text-[10px] text-stone-500">+${a.price} - {a.time}</p>
          </div>
          <div className={`w-9 h-5 rounded-full flex items-center px-0.5 ${a.on ? "bg-emerald-600 justify-end" : "bg-stone-300 justify-start"}`}>
            <div className="w-4 h-4 rounded-full bg-white" />
          </div>
        </div>
      ))}
      <p className="text-[10px] text-stone-500 italic">Adding shows up on your preference card too.</p>
    </SectionWithIndicator>

    {/* Pricing breakdown - agreed total */}
    <SectionWithIndicator title="Pricing agreed" indicator="Total agreed with your host. Tip happens separately at checkout.">
      <div className="space-y-1 text-xs">
        <PriceRow label="Full Glam (service)" amount="$225" />
        <PriceRow label="Strip lashes (add-on)" amount="+$25" tone="emerald" />
        <PriceRow label="Brow shape + tint (add-on)" amount="+$35" tone="emerald" />
        <div className="border-t border-stone-100 my-1.5" />
        <PriceRow label="Subtotal" amount="$285" bold />
        <PriceRow label="Tip suggestion (20%)" amount="$57" tone="stone" subtle />
        <div className="border-t border-stone-100 my-1.5" />
        <PriceRow label="Expected total" amount="$342" bold large />
      </div>
    </SectionWithIndicator>

    {/* Tip payment options */}
    <SectionWithIndicator title="Tip your host" indicator="Tip goes directly to your host. Host sets which methods they accept.">
      <div className="grid grid-cols-2 gap-2">
        {[
          { name: "Zelle", icon: Mail, sub: "+1 (555) 555-0103", color: "purple", testid: "tip-zelle" },
          { name: "Apple Pay", icon: Smartphone, sub: "Tap to share", color: "stone", testid: "tip-apple" },
          { name: "Venmo", icon: ExternalLink, sub: "@shelseamua", color: "indigo", testid: "tip-venmo" },
          { name: "Cash App", icon: ExternalLink, sub: "$ShelseaP", color: "emerald", testid: "tip-cashapp" },
        ].map((t) => (
          <button key={t.name} className="rounded-xl border border-stone-200 p-2.5 text-left flex items-center gap-2 hover:border-stone-400" data-testid={t.testid}>
            <div className={`w-8 h-8 rounded-lg bg-${t.color}-100 text-${t.color}-700 flex items-center justify-center`}><t.icon className="w-3.5 h-3.5" /></div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold text-stone-900">{t.name}</p>
              <p className="text-[9px] text-stone-500 truncate">{t.sub}</p>
            </div>
          </button>
        ))}
      </div>
      <p className="text-[10px] text-stone-400">$57 suggestion - tap to copy + open app</p>
    </SectionWithIndicator>

    {/* Products used - links + indicators */}
    <SectionWithIndicator title="Products used in this look" indicator="Brand or host-owned products. Tap to learn more or shop." headerRight={<span className="text-[10px] text-stone-400">8 products</span>}>
      <div className="grid grid-cols-2 gap-2">
        {[
          { brand: "Fenty Beauty", name: "Pro Filt'r 240", type: "brand", img: "https://images.unsplash.com/photo-1631214540242-5d6c0d6e0fa2?w=200" },
          { brand: "Charlotte Tilbury", name: "Pillow Talk", type: "brand", img: "https://images.unsplash.com/photo-1586495777744-4413f21062fa?w=200" },
          { brand: "Privé Beauté", name: "Glow Powder", type: "host-owned", img: "https://images.unsplash.com/photo-1503236823255-94609f598e71?w=200" },
          { brand: "Ardell", name: "Demi Wispies", type: "brand", img: "https://images.unsplash.com/photo-1583241800698-9c2e89f1d2f4?w=200" },
        ].map((p, i) => (
          <a key={i} href="#" className="rounded-lg border border-stone-200 overflow-hidden bg-white" data-testid={`product-link-${i}`}>
            <div className="aspect-square bg-cover relative" style={{backgroundImage:`url(${p.img})`}}>
              {p.type === "host-owned" && <span className="absolute top-1 left-1 text-[7px] uppercase tracking-wider bg-purple-600 text-white px-1 py-0.5 rounded font-bold">Host owned</span>}
              <ExternalLink className="absolute top-1 right-1 w-3 h-3 text-white drop-shadow" />
            </div>
            <div className="p-1.5">
              <p className="text-[9px] uppercase text-stone-500 truncate">{p.brand}</p>
              <p className="text-[10px] font-semibold text-stone-800 truncate">{p.name}</p>
            </div>
          </a>
        ))}
      </div>
    </SectionWithIndicator>

    {/* Inspo + tutorials linked */}
    <SectionWithIndicator title="Linked tutorials + inspo" indicator="Walkthroughs your host tagged for this look.">
      <div className="space-y-2">
        {[
          { title: "Soft glam step-by-step", url: "youtube.com", tone: "rose" },
          { title: "Lash strip placement", url: "tiktok.com", tone: "indigo" },
        ].map((t,i) => (
          <a key={i} className="flex items-center gap-3 p-2.5 rounded-xl bg-stone-50 border border-stone-100" data-testid={`tutorial-link-${i}`}>
            <div className={`w-10 h-10 rounded-lg bg-${t.tone}-100`} />
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold text-stone-900 truncate">{t.title}</p>
              <p className="text-[9px] text-stone-500">{t.url}</p>
            </div>
            <ExternalLink className="w-3.5 h-3.5 text-stone-400" />
          </a>
        ))}
      </div>
    </SectionWithIndicator>

    <Link to="/design/confirmation-v2?s=host" className="block text-center text-[11px] text-purple-700 font-semibold pt-2">See host control view -</Link>
  </div>
);
const PriceRow = ({ label, amount, tone = "stone", bold, large, subtle }) => (
  <div className="flex items-center justify-between">
    <p className={`${large ? "text-sm" : "text-xs"} ${bold ? "font-bold text-stone-900" : "text-stone-700"} ${subtle ? "opacity-60" : ""}`}>{label}</p>
    <p className={`${large ? "text-base" : "text-xs"} ${bold ? "font-bold" : ""} text-${tone}-900 ${subtle ? "opacity-60" : ""}`}>{amount}</p>
  </div>
);

const Conf2Host = () => (
  <div className="space-y-3" data-testid="state-conf2-host">
    <div className="rounded-2xl bg-gradient-to-br from-purple-700 to-stone-900 text-white p-4">
      <p className="text-[9px] uppercase tracking-wider opacity-70 font-bold">Host control panel - Sister&apos;s wedding glam</p>
      <p className="text-base font-bold mt-1">Manage what Aria sees</p>
      <p className="text-[11px] opacity-80">Updates push to her confirmation + preference card instantly.</p>
    </div>

    <SectionWithIndicator title="Add-ons you offer" indicator="Curate add-ons available for this booking. Toggle visible / hidden." headerRight={<button className="text-[10px] text-purple-700 font-semibold flex items-center gap-1"><Plus className="w-2.5 h-2.5" /> Add</button>}>
      {[
        { name: "Strip lashes", price: 25, on: true },
        { name: "Glow Powder Touch-up Kit", price: 18, on: true },
        { name: "Brow shape + tint", price: 35, on: true },
        { name: "Hair touch-up", price: 45, on: false },
      ].map((a,i) => (
        <div key={i} className="flex items-center gap-2 py-1.5">
          <div className="w-8 h-8 rounded bg-stone-200" />
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold text-stone-900 truncate">{a.name}</p>
            <p className="text-[9px] text-stone-500">${a.price}</p>
          </div>
          <div className={`w-9 h-5 rounded-full flex items-center px-0.5 ${a.on ? "bg-emerald-600 justify-end" : "bg-stone-300 justify-start"}`}>
            <div className="w-4 h-4 rounded-full bg-white" />
          </div>
        </div>
      ))}
    </SectionWithIndicator>

    <SectionWithIndicator title="Products used (you log these)" indicator="What you used on Aria during this service. Auto-tagged to her preference card." headerRight={<button className="text-[10px] text-purple-700 font-semibold flex items-center gap-1"><Plus className="w-2.5 h-2.5" /> Add product</button>}>
      <div className="grid grid-cols-3 gap-2">
        {[
          { brand: "Fenty", name: "240", tag: "Foundation" },
          { brand: "CT", name: "Pillow Talk", tag: "Lip" },
          { brand: "ABH", name: "Soft Glam", tag: "Eyes" },
          { brand: "Privé", name: "Glow Powder", tag: "Highlight", host: true },
          { brand: "Ardell", name: "Demi Wispies", tag: "Lash" },
        ].map((p,i) => (
          <div key={i} className="rounded-lg border border-stone-200 p-1.5 relative">
            {p.host && <span className="absolute top-1 left-1 text-[7px] uppercase bg-purple-600 text-white px-1 rounded font-bold">Yours</span>}
            <div className="aspect-square bg-stone-200 rounded mb-1" />
            <p className="text-[9px] uppercase tracking-wider text-stone-500 truncate">{p.brand}</p>
            <p className="text-[10px] font-semibold text-stone-800 truncate">{p.name}</p>
            <p className="text-[8px] text-stone-400">{p.tag}</p>
          </div>
        ))}
      </div>
    </SectionWithIndicator>

    <SectionWithIndicator title="Tip methods you accept" indicator="Toggle which payment options show on Aria&apos;s confirmation.">
      {[
        { name: "Zelle", val: "+1 (555) 555-0103", on: true, icon: Mail },
        { name: "Apple Pay", val: "Auto via wallet", on: true, icon: Smartphone },
        { name: "Venmo", val: "@shelseamua", on: true, icon: ExternalLink },
        { name: "Cash App", val: "$ShelseaP", on: true, icon: ExternalLink },
        { name: "PayPal", val: "(not set)", on: false, icon: ExternalLink },
      ].map((t,i) => (
        <div key={i} className="flex items-center gap-2 py-1.5">
          <div className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center"><t.icon className="w-3.5 h-3.5 text-stone-600" /></div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold text-stone-900">{t.name}</p>
            <p className="text-[9px] text-stone-500 truncate">{t.val}</p>
          </div>
          <div className={`w-9 h-5 rounded-full flex items-center px-0.5 ${t.on ? "bg-emerald-600 justify-end" : "bg-stone-300 justify-start"}`}>
            <div className="w-4 h-4 rounded-full bg-white" />
          </div>
        </div>
      ))}
    </SectionWithIndicator>

    <SectionWithIndicator title="Tip tracker (manual)" indicator="Manual ledger - automate later with payment integrations.">
      <div className="space-y-1.5">
        {[
          { client: "Aria J.", tipped: 60, method: "Zelle", date: "Jun 15" },
          { client: "Brielle M.", tipped: 40, method: "Venmo", date: "Jun 12" },
          { client: "Camille D.", tipped: "?", method: "—", date: "Jun 08" },
        ].map((row, i) => (
          <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-stone-100 last:border-0">
            <span className="text-stone-700">{row.client}</span>
            <span className="text-stone-500 text-[10px]">{row.method}</span>
            <span className="font-bold text-stone-900">${row.tipped}</span>
            <span className="text-[10px] text-stone-400">{row.date}</span>
          </div>
        ))}
        <button className="w-full mt-2 text-[10px] py-1.5 rounded-md bg-stone-100 text-stone-700 font-semibold">+ Log a tip</button>
      </div>
    </SectionWithIndicator>
  </div>
);

const Conf2Explainer = () => (
  <div className="space-y-3 relative" data-testid="state-conf2-explainer">
    <SectionWithIndicator title="Pricing agreed" indicator="">
      <div className="space-y-1 text-xs">
        <PriceRow label="Full Glam" amount="$225" />
        <PriceRow label="Add-ons" amount="+$60" />
        <PriceRow label="Total" amount="$285" bold large />
      </div>
    </SectionWithIndicator>
    {/* explainer popup overlay */}
    <div className="absolute top-12 left-3 right-3 rounded-2xl bg-stone-900 text-white p-4 shadow-2xl z-30" data-testid="explainer-popover">
      <div className="flex items-center gap-2 mb-2">
        <Info className="w-3.5 h-3.5" />
        <p className="text-[10px] uppercase tracking-wider font-bold">About pricing</p>
      </div>
      <p className="text-xs leading-snug">Your host sets the service price + add-on prices. Add-ons toggle ON/OFF here and feed your preference card automatically. Tip is separate and goes directly to your host - they choose which methods they accept.</p>
      <div className="flex items-center gap-2 mt-3 text-[10px] text-stone-300">
        <ChevronRight className="w-3 h-3" />
        <span>Tap the (i) icon on any section for more</span>
      </div>
      <button className="absolute top-2 right-2 text-stone-400"><Settings className="w-3 h-3" /></button>
    </div>
  </div>
);

// ============================================================================
// 3) PREFERENCE LABEL REFERENCE - how each pref renders on the card
// ============================================================================
const LABEL_FIELDS = [
  // Visual fields
  { key: "foundation_shade", label: "Foundation shade", render: "Color swatch + product thumbnail + brand + shade code", example: "swatch", visual: "rgb(229,184,143)" },
  { key: "skin_tone", label: "Skin tone band", render: "5-band gradient with selected band highlighted", example: "tone-band", band: 3 },
  { key: "undertone", label: "Undertone", render: "Tinted circle: warm / cool / neutral", example: "undertone", value: "warm" },
  { key: "eye_shape", label: "Eye shape", render: "Diagrammatic icon of the shape", example: "eye-icon", value: "almond" },
  { key: "lip_finish", label: "Lip finish", render: "Texture sample (matte / satin / glossy / sheer)", example: "lip-finish", value: "satin" },
  { key: "lash_style", label: "Lash style", render: "Outline icon: natural / wispy / dramatic / mega", example: "lash-icon", value: "natural" },
  { key: "lash_brand_size", label: "Lash brand + size", render: "Brand logo + numeric shade (Ardell - Demi Wispies)", example: "brand-tag" },
  { key: "blush_finish", label: "Blush finish", render: "Color circle: dewy / matte / shimmer", example: "blush-finish" },
  { key: "favorite_products", label: "Favorite products", render: "Heart-marked product tiles with brand + name", example: "heart-tile" },
  // Tag-based fields
  { key: "skin_type", label: "Skin type", render: "Tag pill (combination / dry / oily / sensitive)", example: "tag" },
  { key: "skin_concerns", label: "Skin concerns", render: "Tag pills - multi", example: "tag-multi" },
  { key: "allergies", label: "Allergies", render: "Red-tinted alert tags with allergen icon", example: "allergy-tag" },
  { key: "accessibility", label: "Accessibility", render: "Purple accessibility-icon tags", example: "access-tag" },
  // Text fields
  { key: "special_notes", label: "Special notes", render: "Italic quoted text under heading", example: "quote-text" },
];

const TONES = ["#f3d6c2","#e6b894","#cf9170","#9e6b4d","#5d3b29"];
function PrefLabelDesign() {
  return (
    <div className={FRAME}>
      <Header title="Preference labels" back="/design" />
      <div className="px-4 pt-3 pb-32 space-y-3 bg-stone-50" data-testid="state-pref-labels">
        <div className="rounded-2xl bg-stone-900 text-white p-4">
          <p className="text-[9px] uppercase tracking-wider opacity-70 font-bold">Preference rendering map</p>
          <p className="text-base font-bold mt-1">How each preference shows on the card</p>
          <p className="text-[11px] opacity-80 mt-1">14 fields - based on the selection type, the card renders a visual sample, tag, image, or quoted note.</p>
        </div>
        <div className="space-y-2">
          {LABEL_FIELDS.map((f) => (
            <div key={f.key} className="rounded-2xl bg-white border border-stone-200 p-3" data-testid={`pref-label-${f.key}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1">
                  <p className="text-xs font-bold text-stone-900">{f.label}</p>
                  <p className="text-[10px] text-stone-500 mt-0.5 leading-snug">{f.render}</p>
                </div>
                <span className="text-[8px] uppercase tracking-wider bg-stone-100 text-stone-600 px-1.5 py-0.5 rounded font-bold">{f.example}</span>
              </div>
              {/* Visual sample */}
              <div className="rounded-lg bg-stone-50 border border-stone-100 p-2">
                {f.example === "swatch" && (
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full border-2 border-white shadow" style={{ backgroundColor: f.visual }} />
                    <div className="w-8 h-8 rounded bg-stone-200" />
                    <div className="text-[10px]"><p className="font-bold text-stone-900">Fenty Beauty</p><p className="text-stone-500">Pro Filt&apos;r 240</p></div>
                  </div>
                )}
                {f.example === "tone-band" && (
                  <div className="flex gap-0.5">
                    {TONES.map((t, i) => (
                      <div key={i} className={`flex-1 h-6 rounded ${i === f.band ? "ring-2 ring-stone-900 ring-offset-1" : ""}`} style={{ backgroundColor: t }} />
                    ))}
                  </div>
                )}
                {f.example === "undertone" && (
                  <div className="flex items-center gap-3">
                    {["warm","cool","neutral"].map((u) => (
                      <div key={u} className="flex items-center gap-1">
                        <div className={`w-5 h-5 rounded-full border-2 ${u === f.value ? "border-stone-900" : "border-stone-200"}`} style={{ backgroundColor: u === "warm" ? "#f4cba0" : u === "cool" ? "#e3c0c5" : "#dfd0c5" }} />
                        <span className={`text-[10px] capitalize ${u === f.value ? "font-bold text-stone-900" : "text-stone-500"}`}>{u}</span>
                      </div>
                    ))}
                  </div>
                )}
                {f.example === "eye-icon" && (
                  <div className="flex items-center gap-2">
                    <svg viewBox="0 0 64 32" className="w-16 h-8 text-stone-900"><path d="M2 16 Q16 4 32 4 T62 16 Q48 28 32 28 T2 16 Z" fill="none" stroke="currentColor" strokeWidth="1.5" /><circle cx="32" cy="16" r="6" fill="currentColor" /></svg>
                    <span className="text-[10px] font-bold text-stone-900 capitalize">{f.value}</span>
                  </div>
                )}
                {f.example === "lip-finish" && (
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-5 rounded-full" style={{ background: f.value === "satin" ? "linear-gradient(135deg, #c4756a, #a85a51)" : "#b85a51" }} />
                    <span className="text-[10px] font-bold text-stone-900 capitalize">{f.value}</span>
                  </div>
                )}
                {f.example === "lash-icon" && (
                  <div className="flex items-center gap-2">
                    <svg viewBox="0 0 48 16" className="w-12 h-4 text-stone-900">
                      {[3,8,13,19,25,31,37,43].map((x,i) => <line key={i} x1={x} y1="14" x2={x + 2} y2="2" stroke="currentColor" strokeWidth="1.2" />)}
                      <line x1="0" y1="14" x2="48" y2="14" stroke="currentColor" strokeWidth="1.5" />
                    </svg>
                    <span className="text-[10px] font-bold text-stone-900 capitalize">{f.value} volume</span>
                  </div>
                )}
                {f.example === "brand-tag" && (
                  <div className="flex items-center gap-1.5">
                    <div className="text-[10px] bg-stone-900 text-white px-2 py-0.5 rounded-full font-bold">Ardell</div>
                    <div className="text-[10px] bg-stone-100 text-stone-700 px-2 py-0.5 rounded-full">Demi Wispies</div>
                    <div className="text-[10px] bg-stone-100 text-stone-700 px-2 py-0.5 rounded-full">11mm</div>
                  </div>
                )}
                {f.example === "blush-finish" && (
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full" style={{ background: "radial-gradient(circle at 30% 30%, #f0a4a2, #d76c69)" }} />
                    <span className="text-[10px] font-bold text-stone-900">Dewy - cream</span>
                  </div>
                )}
                {f.example === "heart-tile" && (
                  <div className="grid grid-cols-3 gap-1">
                    {[1,2,3].map(i => (
                      <div key={i} className="rounded-md bg-stone-200 aspect-square relative">
                        <Heart className="absolute top-0.5 right-0.5 w-2.5 h-2.5 text-rose-500" fill="currentColor" />
                      </div>
                    ))}
                  </div>
                )}
                {f.example === "tag" && (
                  <span className="text-[10px] bg-stone-900 text-white px-2 py-0.5 rounded-full font-bold">Combination</span>
                )}
                {f.example === "tag-multi" && (
                  <div className="flex gap-1 flex-wrap">
                    {["Pores","Fine lines","Hyperpigmentation"].map(t => <span key={t} className="text-[10px] bg-stone-100 text-stone-700 px-2 py-0.5 rounded-full">{t}</span>)}
                  </div>
                )}
                {f.example === "allergy-tag" && (
                  <div className="flex gap-1 flex-wrap">
                    {["Peanuts","Latex","Pollen"].map(t => <span key={t} className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold flex items-center gap-1"><Zap className="w-2.5 h-2.5" /> {t}</span>)}
                  </div>
                )}
                {f.example === "access-tag" && (
                  <div className="flex gap-1 flex-wrap">
                    <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold">Wheelchair accessible</span>
                    <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold">Low fragrance</span>
                  </div>
                )}
                {f.example === "quote-text" && (
                  <p className="text-[11px] italic text-stone-700">&ldquo;Please use only fragrance-free products. Pregnancy-safe ingredients only.&rdquo;</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <StateSwitcher flow="pref-labels" states={[{id:"main",label:"Reference map"}]} current="main" />
    </div>
  );
}

// ============================================================================
// 4) HOST INSIGHTS DASHBOARD - Spotify Wrapped style
// ============================================================================
const HI_STATES = [
  { id: "main", label: "Main dashboard" },
  { id: "clients", label: "Clients at a glance" },
  { id: "products", label: "Top products used" },
];
function HostInsightsDesign() {
  const [params] = useSearchParams();
  const s = params.get("s") || "main";
  return (
    <div className={FRAME}>
      <Header title="Insights" back="/design" right={<RefreshCw className="w-3.5 h-3.5 text-stone-500" />} />
      <div className="bg-gradient-to-b from-purple-50 via-rose-50 to-amber-50 min-h-screen">
        <div className="px-4 pt-3 pb-32 space-y-3">
          {s === "main" && <HIMain />}
          {s === "clients" && <HIClients />}
          {s === "products" && <HIProducts />}
        </div>
      </div>
      <StateSwitcher flow="host-insights" states={HI_STATES} current={s} />
    </div>
  );
}
const InsightCard = ({ children, gradient, testid }) => (
  <div className={`rounded-3xl p-5 text-white shadow-sm relative overflow-hidden ${gradient || "bg-stone-900"}`} data-testid={testid}>
    {children}
  </div>
);
const HIMain = () => (
  <div className="space-y-3" data-testid="state-hi-main">
    {/* Header - last updated */}
    <div className="flex items-center justify-between">
      <div>
        <p className="text-[9px] uppercase tracking-wider text-stone-500 font-bold">Last updated</p>
        <p className="text-[11px] text-stone-700 font-semibold">Jun 9, 2026 - 9:42 PM</p>
      </div>
      <button className="text-[10px] text-purple-700 font-semibold flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Refresh</button>
    </div>

    {/* Hero - total clients */}
    <InsightCard gradient="bg-gradient-to-br from-purple-700 to-pink-600" testid="hi-total-clients">
      <p className="text-[10px] uppercase tracking-wider opacity-80 font-bold">Your clients</p>
      <p className="text-[88px] leading-none font-black mt-1 -mb-2">142</p>
      <p className="text-sm opacity-90 mt-3">Booked in the last 12 months</p>
      <div className="mt-3 flex items-center gap-2 text-[11px]">
        <TrendingUp className="w-3.5 h-3.5 text-emerald-300" />
        <span className="opacity-90">+24 vs last year</span>
      </div>
    </InsightCard>

    {/* Repeat clients - PFP row */}
    <InsightCard gradient="bg-stone-900">
      <p className="text-[10px] uppercase tracking-wider opacity-70 font-bold">Repeat clients</p>
      <p className="text-3xl font-black mt-1">38</p>
      <p className="text-[11px] opacity-80 mt-1">Booked 2+ times</p>
      <div className="flex -space-x-2 mt-3">
        {[
          "https://images.unsplash.com/photo-1502823403499-6ccfcf4fb453?w=200",
          "https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=200",
          "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=200",
          "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200",
          "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200",
          "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200",
        ].map((u,i) => <div key={i} className="w-9 h-9 rounded-full bg-cover border-2 border-stone-900" style={{backgroundImage:`url(${u})`}} />)}
        <div className="w-9 h-9 rounded-full bg-white/20 border-2 border-stone-900 flex items-center justify-center text-[10px] font-bold">+32</div>
      </div>
    </InsightCard>

    {/* Most requested services + looks */}
    <InsightCard gradient="bg-white text-stone-900 border border-stone-200">
      <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Most requested</p>
      <p className="text-base font-bold mt-1">Full Glam wins again</p>
      <p className="text-[10px] text-stone-500 mt-0.5">42 of 142 bookings (30%)</p>
      <div className="grid grid-cols-3 gap-1.5 mt-3">
        {[
          "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=200",
          "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=200",
          "https://images.unsplash.com/photo-1492106087820-71f1a00d2b11?w=200",
        ].map((u,i) => <div key={i} className="aspect-square rounded-lg bg-cover" style={{backgroundImage:`url(${u})`}} />)}
      </div>
      {/* Stacked list */}
      <div className="mt-3 space-y-1.5">
        {[
          { name: "Full Glam", n: 42, pct: 30 },
          { name: "Bridal Trial", n: 28, pct: 20 },
          { name: "Soft glam", n: 21, pct: 15 },
          { name: "Touch-Up", n: 14, pct: 10 },
        ].map((r,i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="w-4 text-[10px] text-stone-400 font-bold">{i+1}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between text-[11px]">
                <span className="font-semibold text-stone-800">{r.name}</span>
                <span className="text-stone-500">{r.n}</span>
              </div>
              <div className="h-1 bg-stone-100 rounded-full overflow-hidden mt-0.5">
                <div className="h-full bg-purple-500" style={{ width: `${r.pct * 2}%` }} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </InsightCard>

    {/* Top inspo saved by clients */}
    <InsightCard gradient="bg-gradient-to-br from-rose-500 to-amber-500">
      <p className="text-[10px] uppercase tracking-wider opacity-80 font-bold">Most-saved from your inspo</p>
      <p className="text-base font-bold mt-1">Soft glam combo</p>
      <p className="text-[10px] opacity-90 mt-0.5">Saved by 27 clients</p>
      <div className="grid grid-cols-4 gap-1.5 mt-3">
        {Array.from({length:8}).map((_,i) => (
          <div key={i} className="aspect-square rounded-md bg-cover" style={{backgroundImage:`url(https://images.unsplash.com/photo-${["1599733589046-77fde6f1f5b6","1487412947147-5cebf100ffc2","1503236823255-94609f598e71","1620916566398-39f1143ab7be","1583241800698-9c2e89f1d2f4","1512496015851-a90fb38ba796","1531746020798-e6953c6e8e04","1492106087820-71f1a00d2b11"][i]}?w=200)`}} />
        ))}
      </div>
    </InsightCard>

    {/* Link clicks */}
    <InsightCard gradient="bg-white text-stone-900 border border-stone-200">
      <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Link clicks (confirmation page)</p>
      <p className="text-base font-bold mt-1">412 product clicks this month</p>
      <div className="mt-3 space-y-2">
        {[
          { n: 84, t: "Charlotte Tilbury - Pillow Talk", u: "charlottetilbury.com", tone: "rose" },
          { n: 62, t: "Privé Beauté - Glow Powder", u: "host owned", tone: "purple", owned: true },
          { n: 47, t: "Fenty Beauty - Pro Filt'r 240", u: "fentybeauty.com", tone: "stone" },
          { n: 38, t: "Ardell - Demi Wispies", u: "ardell.com", tone: "amber" },
        ].map((l,i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-10 text-right">
              <p className="text-base font-black text-stone-900">{l.n}</p>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold text-stone-800 truncate flex items-center gap-1">
                {l.t}
                {l.owned && <span className="text-[7px] uppercase bg-purple-600 text-white px-1 rounded font-bold">Yours</span>}
              </p>
              <p className="text-[9px] text-stone-500">{l.u}</p>
            </div>
            <ExternalLink className="w-3 h-3 text-stone-300" />
          </div>
        ))}
      </div>
    </InsightCard>

    {/* Top foundation shades from clients - visual color row */}
    <Link to="/design/host-insights?s=clients" className="block">
      <InsightCard gradient="bg-stone-900">
        <p className="text-[10px] uppercase tracking-wider opacity-70 font-bold">Clients at a glance</p>
        <p className="text-base font-bold mt-1">Foundation shades you serve</p>
        <div className="flex items-end gap-1 mt-3 h-12">
          {[
            { c: "#f4d9bc", n: 18 },
            { c: "#e8bb96", n: 24 },
            { c: "#c89671", n: 31 },
            { c: "#a06f53", n: 22 },
            { c: "#7a4f3a", n: 16 },
            { c: "#553323", n: 9 },
          ].map((s,i) => (
            <div key={i} className="flex-1 flex flex-col items-center">
              <div className="w-full rounded-t-md" style={{ height: `${s.n*1.4}px`, backgroundColor: s.c }} />
              <p className="text-[9px] opacity-70 mt-1">{s.n}</p>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between mt-3 text-[10px] opacity-80">
          <span>Light to deep</span>
          <span className="flex items-center gap-1">See all <ChevronRight className="w-3 h-3" /></span>
        </div>
      </InsightCard>
    </Link>

    {/* Top preferences cloud */}
    <InsightCard gradient="bg-white text-stone-900 border border-stone-200">
      <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Top preferences this quarter</p>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {[
          { t: "Soft glam", size: 18, color: "purple" },
          { t: "Natural volume lashes", size: 16, color: "rose" },
          { t: "Satin lips", size: 14, color: "amber" },
          { t: "Warm undertone", size: 14, color: "stone" },
          { t: "Dewy finish", size: 12, color: "emerald" },
          { t: "Almond eyes", size: 11, color: "indigo" },
          { t: "Nude glam", size: 11, color: "rose" },
          { t: "Winged liner", size: 10, color: "stone" },
          { t: "Combination skin", size: 9, color: "amber" },
          { t: "Pores", size: 8, color: "stone" },
          { t: "Fine lines", size: 7, color: "stone" },
        ].map((p,i) => (
          <span key={i} className={`px-2 py-1 rounded-full bg-${p.color}-100 text-${p.color}-700 font-bold`} style={{ fontSize: `${p.size * 0.7}px` }}>{p.t}</span>
        ))}
      </div>
    </InsightCard>

    <p className="text-[10px] text-stone-400 text-center pt-2">Phygital insights - this is your year in beauty.</p>
  </div>
);

const HIClients = () => (
  <div className="space-y-3" data-testid="state-hi-clients">
    <Link to="/design/host-insights?s=main" className="text-[11px] text-stone-500 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Insights</Link>
    <div>
      <h3 className="text-xl font-black text-stone-900">Your clients at a glance</h3>
      <p className="text-[11px] text-stone-500">All visual - the way you actually think about your work.</p>
    </div>
    {/* Foundation palette */}
    <InsightCard gradient="bg-white text-stone-900 border border-stone-200">
      <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Top foundation shades</p>
      <div className="grid grid-cols-6 gap-1.5 mt-3">
        {["#f4d9bc","#ecc69d","#dfae84","#c89671","#b07d5a","#945e44","#7a4f3a","#5e3a2a","#4a2d1f","#382016","#2c1810","#1a0d08"].map((c,i) => (
          <div key={i} className="aspect-square rounded-full shadow-sm" style={{ backgroundColor: c }} title={c} />
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2 text-[10px] text-stone-500"><Info className="w-3 h-3" /> Each circle = one shade in your client base</div>
    </InsightCard>
    {/* Products they love */}
    <InsightCard gradient="bg-white text-stone-900 border border-stone-200">
      <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Products they love</p>
      <div className="grid grid-cols-3 gap-2 mt-3">
        {[
          { brand: "Charlotte Tilbury", n: "Pillow Talk", img: "https://images.unsplash.com/photo-1586495777744-4413f21062fa?w=200", count: 28 },
          { brand: "Fenty Beauty", n: "Pro Filt'r 240", img: "https://images.unsplash.com/photo-1631214540242-5d6c0d6e0fa2?w=200", count: 22 },
          { brand: "Anastasia", n: "Soft Glam", img: "https://images.unsplash.com/photo-1512496015851-a90fb38ba796?w=200", count: 19 },
          { brand: "Glossier", n: "Cloud Paint", img: "https://images.unsplash.com/photo-1631214500099-1b0d8db6e0bd?w=200", count: 17 },
          { brand: "Fenty Beauty", n: "Killawatt", img: "https://images.unsplash.com/photo-1503236823255-94609f598e71?w=200", count: 14 },
          { brand: "MAC", n: "Velvet Teddy", img: "https://images.unsplash.com/photo-1599733589046-77fde6f1f5b6?w=200", count: 12 },
        ].map((p,i) => (
          <div key={i} className="relative">
            <div className="aspect-square bg-cover rounded-lg" style={{backgroundImage:`url(${p.img})`}} />
            <Heart className="absolute top-1 right-1 w-3 h-3 text-rose-500" fill="currentColor" />
            <p className="text-[9px] uppercase tracking-wider text-stone-500 truncate mt-1">{p.brand}</p>
            <p className="text-[10px] font-bold text-stone-800 truncate">{p.n}</p>
            <p className="text-[9px] text-stone-400">{p.count} clients</p>
          </div>
        ))}
      </div>
    </InsightCard>
    {/* Favorite brands - logos */}
    <InsightCard gradient="bg-white text-stone-900 border border-stone-200">
      <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Favorite brands</p>
      <div className="grid grid-cols-4 gap-2 mt-3">
        {[
          { b: "Fenty", c: "bg-amber-200 text-amber-900", count: 38 },
          { b: "CT", c: "bg-rose-200 text-rose-900", count: 31 },
          { b: "MAC", c: "bg-stone-900 text-white", count: 27 },
          { b: "ABH", c: "bg-stone-200 text-stone-900", count: 24 },
          { b: "Dior", c: "bg-stone-100 text-stone-900", count: 18 },
          { b: "Glossier", c: "bg-pink-200 text-pink-900", count: 16 },
          { b: "NARS", c: "bg-red-200 text-red-900", count: 14 },
          { b: "Ardell", c: "bg-emerald-100 text-emerald-900", count: 12 },
        ].map((b,i) => (
          <div key={i} className="aspect-square flex flex-col items-center justify-center rounded-xl text-center">
            <div className={`w-full h-full ${b.c} rounded-xl flex flex-col items-center justify-center font-black`}>
              <span className="text-sm">{b.b}</span>
              <span className="text-[8px] uppercase font-normal opacity-70 mt-0.5">{b.count}</span>
            </div>
          </div>
        ))}
      </div>
    </InsightCard>
    {/* Eye shapes */}
    <InsightCard gradient="bg-white text-stone-900 border border-stone-200">
      <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Eye shapes you work with</p>
      <div className="grid grid-cols-4 gap-2 mt-3">
        {[
          { shape: "almond", n: 52 },
          { shape: "hooded", n: 31 },
          { shape: "round", n: 22 },
          { shape: "monolid", n: 19 },
          { shape: "deep set", n: 14 },
          { shape: "upturned", n: 9 },
          { shape: "downturned", n: 7 },
          { shape: "wide set", n: 5 },
        ].map((e,i) => (
          <div key={i} className="rounded-lg border border-stone-100 p-2 text-center">
            <svg viewBox="0 0 64 32" className="w-full h-6 text-stone-900"><path d="M2 16 Q16 6 32 6 T62 16 Q48 26 32 26 T2 16 Z" fill="none" stroke="currentColor" strokeWidth="1.5" /><circle cx="32" cy="16" r="5" fill="currentColor" /></svg>
            <p className="text-[9px] font-bold capitalize mt-1">{e.shape}</p>
            <p className="text-[8px] text-stone-500">{e.n}</p>
          </div>
        ))}
      </div>
    </InsightCard>
    {/* Lash types + brands */}
    <InsightCard gradient="bg-white text-stone-900 border border-stone-200">
      <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Favorite lash sets</p>
      <div className="space-y-2 mt-3">
        {[
          { brand: "Ardell", style: "Demi Wispies", n: 28, mm: "11mm" },
          { brand: "Lilly Lashes", style: "Miami", n: 18, mm: "13mm" },
          { brand: "Velour", style: "Effortless", n: 14, mm: "10mm" },
          { brand: "House of Lashes", style: "Iconic", n: 11, mm: "12mm" },
        ].map((l,i) => (
          <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-stone-50">
            <div className="w-9 h-9 rounded-full bg-stone-900 flex items-center justify-center"><Star className="w-3.5 h-3.5 text-white" /></div>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-wider text-stone-500">{l.brand}</p>
              <p className="text-xs font-bold text-stone-900">{l.style}</p>
            </div>
            <span className="text-[10px] text-stone-700 bg-stone-200 px-2 py-0.5 rounded-full font-bold">{l.mm}</span>
            <span className="text-[10px] text-stone-500 w-8 text-right">{l.n}</span>
          </div>
        ))}
      </div>
    </InsightCard>
    {/* Allergies + accessibility */}
    <InsightCard gradient="bg-white text-stone-900 border border-stone-200">
      <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Watch-outs you serve</p>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div>
          <p className="text-[10px] font-bold text-red-700 mb-1.5">Allergies</p>
          <div className="space-y-1">
            {[{t:"Latex",n:9},{t:"Fragrance",n:7},{t:"Pollen",n:5}].map((a,i)=>(
              <div key={i} className="flex items-center justify-between text-[10px] bg-red-50 rounded px-2 py-1">
                <span className="text-red-800 font-bold">{a.t}</span>
                <span className="text-red-500">{a.n}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <p className="text-[10px] font-bold text-purple-700 mb-1.5">Accessibility</p>
          <div className="space-y-1">
            {[{t:"Wheelchair",n:4},{t:"Low fragrance",n:8},{t:"Pregnancy-safe",n:6}].map((a,i)=>(
              <div key={i} className="flex items-center justify-between text-[10px] bg-purple-50 rounded px-2 py-1">
                <span className="text-purple-800 font-bold">{a.t}</span>
                <span className="text-purple-500">{a.n}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </InsightCard>
  </div>
);

const HIProducts = () => (
  <div className="space-y-3" data-testid="state-hi-products">
    <Link to="/design/host-insights?s=main" className="text-[11px] text-stone-500 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Insights</Link>
    <div>
      <h3 className="text-xl font-black text-stone-900">Top products you use</h3>
      <p className="text-[11px] text-stone-500">Soon to power your prep + budget plan automatically.</p>
    </div>
    <InsightCard gradient="bg-white text-stone-900 border border-stone-200">
      <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Most used in services</p>
      <div className="space-y-2 mt-3">
        {[
          { brand: "Fenty Beauty", name: "Pro Filt'r 240", img: "https://images.unsplash.com/photo-1631214540242-5d6c0d6e0fa2?w=200", n: 84, low: false },
          { brand: "Charlotte Tilbury", name: "Pillow Talk Lipstick", img: "https://images.unsplash.com/photo-1586495777744-4413f21062fa?w=200", n: 64 },
          { brand: "Privé Beauté", name: "Glow Powder (yours)", img: "https://images.unsplash.com/photo-1503236823255-94609f598e71?w=200", n: 58, owned: true },
          { brand: "Anastasia", name: "Soft Glam Palette", img: "https://images.unsplash.com/photo-1512496015851-a90fb38ba796?w=200", n: 49, low: true },
          { brand: "Ardell", name: "Demi Wispies", img: "https://images.unsplash.com/photo-1583241800698-9c2e89f1d2f4?w=200", n: 38 },
          { brand: "Urban Decay", name: "All Nighter Setting Spray", img: "https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=200", n: 27 },
        ].map((p,i) => (
          <div key={i} className="flex items-center gap-3 p-2 rounded-xl bg-stone-50 border border-stone-100">
            <div className="w-12 h-12 rounded-lg bg-cover" style={{backgroundImage:`url(${p.img})`}} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                <p className="text-[10px] uppercase tracking-wider text-stone-500 truncate">{p.brand}</p>
                {p.owned && <span className="text-[7px] uppercase bg-purple-600 text-white px-1 rounded font-bold">Yours</span>}
                {p.low && <span className="text-[7px] uppercase bg-amber-500 text-white px-1 rounded font-bold">Low</span>}
              </div>
              <p className="text-xs font-bold text-stone-900 truncate">{p.name}</p>
            </div>
            <div className="text-right">
              <p className="text-base font-black text-stone-900">{p.n}</p>
              <p className="text-[8px] uppercase text-stone-500">used</p>
            </div>
          </div>
        ))}
      </div>
    </InsightCard>
    <InsightCard gradient="bg-gradient-to-br from-amber-500 to-rose-500">
      <Award className="w-5 h-5 mb-1" />
      <p className="text-[10px] uppercase tracking-wider opacity-80 font-bold">Coming next</p>
      <p className="text-base font-bold mt-1">Auto-prep + budget plan</p>
      <p className="text-[11px] opacity-90 mt-1">We&apos;ll predict what you&apos;ll need next month from your usage. Restock alerts before you run out.</p>
    </InsightCard>
  </div>
);

// ============================================================================
export default function R19Designs({ flow }) {
  if (flow === "inspo-v2") return <InspoV2Design />;
  if (flow === "confirmation-v2") return <ConfirmationV2Design />;
  if (flow === "pref-labels") return <PrefLabelDesign />;
  if (flow === "host-insights") return <HostInsightsDesign />;
  return null;
}
